import type {
  AccessoryRecommendation,
  EquipmentAccessoryData
} from 'components/accessories-page/accessory-type-panel';
import type {
  AccessoryProductType,
  AccessoryTypeType,
  AccessoryWithQuantityType
} from 'data/types';
import type {
  QuotableSystemAccessoryRecommendationType,
  RequiredAccessoryGroupType,
  RequiredAccessoryOptionType
} from 'features/api/accessories-api-types';
import type { AccessorySelectionChangeType } from 'utils/accessory-option-groups-types';
import {
  AccessoryGroup,
  AccessoryOption,
  AccessoryPick,
  isSameAccessoryPick,
  offeredAccessoryOptions
} from 'utils/ducted-accessory-groups';
import {
  DuctedAccessoryPanelDataType,
  DuctedAccessorySelectionsType,
  DuctedAccessoryUnitType,
  DuctedLineSetOptionType,
  SystemAccessoryTypeType
} from 'utils/ducted-accessories-types';
import { buildSavedRowProductLookup } from 'utils/manual-accessories';
import type { ManualAccessorySourceType } from 'utils/manual-accessories-types';

export const LINE_SET_ACCESSORY_TYPE_CODE = 'line_set';
export const RECOMMENDED_ACCESSORY_PRIORITY = 1;

export const toAccessoryProduct = (
  rec: QuotableSystemAccessoryRecommendationType
): AccessoryProductType => ({
  productId: rec.accessoryProductId,
  name: rec.accessoryName,
  description: rec.accessoryDescription,
  price: rec.accessoryPrice,
  type: rec.accessoryOriginalType as AccessoryTypeType,
  productUrl: rec.accessoryProductUrl,
  photo: rec.accessoryPhoto,
  availableStock: rec.accessoryAvailableStock
});

const toPanelRecommendation = (
  rec: QuotableSystemAccessoryRecommendationType
): AccessoryRecommendation => ({
  systemUniqueId: rec.systemUniqueId,
  productType: rec.productType,
  productId: rec.productId,
  uniqueId: rec.uniqueId,
  accessoryProductId: rec.accessoryProductId,
  accessoryType: rec.accessoryType,
  recommendedPriority: rec.recommendedPriority,
  recommendedQuantity: rec.recommendedQuantity,
  lineSetSettings: rec.lineSetSettings
});

export const isLineSetRecommendation = (
  rec: QuotableSystemAccessoryRecommendationType
): boolean => rec.accessoryType === LINE_SET_ACCESSORY_TYPE_CODE;

export const requiredOptionProductIds = (
  groups: RequiredAccessoryGroupType[]
): Set<number> =>
  new Set(
    groups.flatMap((group) =>
      group.options.map((option) => option.alpineProductId)
    )
  );

const toAccessoryOption = (
  option: RequiredAccessoryOptionType
): AccessoryOption => ({
  accessoryId: option.accessoryId,
  typeId: option.typeId,
  enriched: {
    accessoryTypeCode: option.accessoryTypeCode,
    accessoryTypeLabel: option.accessoryTypeLabel,
    alpineProductId: option.alpineProductId,
    manufacturer: option.manufacturer,
    modelNumber: option.modelNumber,
    heatingCapacityBtu: option.heatingCapacityBtu ?? null,
    product: option.product
  }
});

export function toRequiredAccessoryGroups(
  groups: RequiredAccessoryGroupType[]
): AccessoryGroup[] {
  return groups
    .map((group) => ({
      accessoryTypeCode: group.accessoryTypeCode,
      accessoryTypeLabel: group.accessoryTypeLabel,
      options: offeredAccessoryOptions(
        group.options
          .filter((option) => option.product !== null)
          .map(toAccessoryOption)
      )
    }))
    .filter((group) => group.options.length > 0);
}

export function buildDuctedLineSetOptions(
  recommendations: QuotableSystemAccessoryRecommendationType[]
): DuctedLineSetOptionType[] {
  return recommendations
    .filter(
      (
        rec
      ): rec is QuotableSystemAccessoryRecommendationType & {
        linesetLengthFeet: number;
      } => isLineSetRecommendation(rec) && rec.linesetLengthFeet !== undefined
    )
    .map((rec) => ({
      product: toAccessoryProduct(rec),
      lengthFeet: rec.linesetLengthFeet,
      recommendedPriority: rec.recommendedPriority
    }))
    .sort(
      (a, b) => a.lengthFeet - b.lengthFeet || a.product.price - b.product.price
    );
}

export const findSelectedLineSetProductId = (
  accessories: AccessoryWithQuantityType[],
  options: DuctedLineSetOptionType[]
): number | null =>
  options.find((option) =>
    accessories.some(
      (acc) => acc.productId === option.product.productId && acc.quantity > 0
    )
  )?.product.productId ?? null;

export const findRecommendedLineSetProductId = (
  options: DuctedLineSetOptionType[]
): number | null =>
  options.find(
    (option) => option.recommendedPriority === RECOMMENDED_ACCESSORY_PRIORITY
  )?.product.productId ?? null;

type BuildPanelsParams = {
  recommendations: QuotableSystemAccessoryRecommendationType[];
  accessoryTypes: SystemAccessoryTypeType[];
  selections: DuctedAccessorySelectionsType;
  systemName: string;
  units: DuctedAccessoryUnitType[];
};

type PanelEquipmentSource = DuctedAccessoryUnitType & {
  selectedAccessories: AccessoryWithQuantityType[];
};

const toPanelSelectedAccessories = (
  accessories: AccessoryWithQuantityType[]
): EquipmentAccessoryData['selectedAccessories'] =>
  accessories.map((acc) => ({
    productId: acc.productId,
    quantity: acc.quantity,
    type: acc.type
  }));

const toPanelEquipment = (
  source: PanelEquipmentSource,
  recommendations: QuotableSystemAccessoryRecommendationType[]
): EquipmentAccessoryData[] => {
  const equipmentRecs = recommendations.filter(
    (rec) => rec.uniqueId === source.uniqueId
  );
  if (equipmentRecs.length === 0) return [];

  const accessoryProducts = new Map<number, AccessoryProductType>();
  equipmentRecs.forEach((rec) => {
    accessoryProducts.set(rec.accessoryProductId, toAccessoryProduct(rec));
  });
  return [
    {
      equipmentUniqueId: source.uniqueId,
      equipmentName: source.name,
      condenserUniqueId: source.uniqueId,
      recommendations: equipmentRecs.map(toPanelRecommendation),
      selectedAccessories: toPanelSelectedAccessories(
        source.selectedAccessories
      ),
      accessoryProducts
    }
  ];
};

const hasSelectedRecommendation = (item: EquipmentAccessoryData): boolean =>
  item.recommendations.some((rec) =>
    item.selectedAccessories.some(
      (acc) => acc.productId === rec.accessoryProductId && acc.quantity > 0
    )
  );

export function buildDuctedAccessoryPanels({
  recommendations,
  accessoryTypes,
  selections,
  systemName,
  units
}: BuildPanelsParams): DuctedAccessoryPanelDataType[] {
  const sources: PanelEquipmentSource[] = [
    ...units.map((unit) => ({
      ...unit,
      selectedAccessories: selections.units[unit.uniqueId] ?? []
    })),
    {
      uniqueId: selections.systemUniqueId,
      name: systemName,
      selectedAccessories: selections.system
    }
  ];

  return accessoryTypes
    .filter((type) => type.code !== LINE_SET_ACCESSORY_TYPE_CODE)
    .map((type) => {
      const typeRecs = recommendations.filter(
        (rec) => rec.accessoryType === type.code
      );
      const equipment = sources.flatMap((source) =>
        toPanelEquipment(source, typeRecs)
      );
      return {
        accessoryType: type,
        equipment,
        isCompleted: equipment.some(hasSelectedRecommendation)
      };
    })
    .filter((panel) => panel.equipment.length > 0);
}

export function setAccessoryQuantity(
  accessories: AccessoryWithQuantityType[],
  product: AccessoryProductType,
  quantity: number
): AccessoryWithQuantityType[] {
  const others = accessories.filter(
    (acc) => acc.productId !== product.productId
  );
  if (quantity <= 0) return others;

  return [...others, { ...product, quantity }];
}

export function applyAccessoryQuantityChanges(
  accessories: AccessoryWithQuantityType[],
  changes: AccessorySelectionChangeType[]
): AccessoryWithQuantityType[] {
  return changes.reduce(
    (current, { accessory, quantity }) =>
      setAccessoryQuantity(current, accessory, quantity),
    accessories
  );
}

export function replaceLineSet(
  accessories: AccessoryWithQuantityType[],
  options: DuctedLineSetOptionType[],
  productId: number | null
): AccessoryWithQuantityType[] {
  const lineSetIds = new Set(options.map((option) => option.product.productId));
  const others = accessories.filter((acc) => !lineSetIds.has(acc.productId));
  const chosen = options.find(
    (option) => option.product.productId === productId
  );
  if (!chosen) return others;

  return setAccessoryQuantity(others, chosen.product, 1);
}

export const selectedQuantityOf = (
  accessories: AccessoryWithQuantityType[],
  productId: number
): number =>
  accessories.find((acc) => acc.productId === productId)?.quantity ?? 0;

export const toRequiredAccessory = (
  group: AccessoryGroup,
  option: AccessoryOption
): AccessoryWithQuantityType => {
  const { enriched } = option;
  return {
    productId: enriched.alpineProductId,
    name:
      enriched.product?.name ??
      `${enriched.manufacturer} ${enriched.modelNumber}`,
    description: enriched.product?.description ?? '',
    price: enriched.product?.price ?? 0,
    type: group.accessoryTypeCode as AccessoryTypeType,
    productUrl: enriched.product?.productUrl ?? '',
    photo: enriched.product?.photo ?? '',
    availableStock: enriched.product?.fulfillableStock ?? 0,
    quantity: 1
  };
};

export function applyRequiredPick(
  accessories: AccessoryWithQuantityType[],
  group: AccessoryGroup,
  pick: AccessoryPick
): AccessoryWithQuantityType[] {
  const option = group.options.find((candidate) =>
    isSameAccessoryPick(candidate, pick)
  );
  if (!option) return accessories;

  const groupProductIds = new Set(
    group.options.map((candidate) => candidate.enriched.alpineProductId)
  );
  const others = accessories.filter(
    (acc) => !groupProductIds.has(acc.productId)
  );
  return [...others, toRequiredAccessory(group, option)];
}

export function buildSavedAccessoryProductLookup(
  recommendations: QuotableSystemAccessoryRecommendationType[],
  requiredGroups: RequiredAccessoryGroupType[],
  fallback: AccessoryWithQuantityType[],
  savedRows: ManualAccessorySourceType[] = []
): (productId: number) => AccessoryProductType | undefined {
  const recommendationProducts = new Map(
    recommendations.map((rec) => [
      rec.accessoryProductId,
      toAccessoryProduct(rec)
    ])
  );
  const requiredProducts = new Map(
    toRequiredAccessoryGroups(requiredGroups).flatMap((group) =>
      group.options.map(
        (option) =>
          [
            option.enriched.alpineProductId,
            toRequiredAccessory(group, option)
          ] as const
      )
    )
  );
  const fallbackProducts = new Map(fallback.map((acc) => [acc.productId, acc]));
  const savedRowProducts = buildSavedRowProductLookup(savedRows);

  return (productId) =>
    recommendationProducts.get(productId) ??
    requiredProducts.get(productId) ??
    fallbackProducts.get(productId) ??
    savedRowProducts.get(productId);
}
